import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import ShareIcon from '../../../../../../generic/icons/IconLib/ShareIcon'
import DownloadIcon from '../../../../../../generic/icons/IconLib/DownloadIcon'
import EyeIcon from '../../../../../../generic/icons/IconLib/EyeIcon'
import XIcon from '../../../../../../generic/icons/IconLib/XIcon'

interface IPdfViewerProps {
  file: Blob
  handleSharePdf: () => void
  handleDownloadPdf: () => void
  handleOpenPdf?: () => void
  handleClearFile: () => void
}

const PdfViewer = ({
  file, handleSharePdf, handleDownloadPdf, handleClearFile,
}: IPdfViewerProps) => {
  const blobUrl = useMemo(() => URL.createObjectURL(file), [file])
  useEffect(() => () => URL.revokeObjectURL(blobUrl), [blobUrl])

  return (
    <Container>
      <IFrame src={blobUrl} />
      <ButtonRow>
        <IconAction
          as='a'
          href={blobUrl}
          target='_blank'
          rel='noopener noreferrer'
          aria-label='open'
        >
          <EyeIcon />
        </IconAction>
        <IconAction type='button' onClick={handleDownloadPdf} aria-label='download'>
          <DownloadIcon />
        </IconAction>
        <IconAction type='button' onClick={handleSharePdf} aria-label='share'>
          <ShareIcon />
        </IconAction>
        <IconAction type='button' onClick={handleClearFile} aria-label='close' $danger>
          <XIcon />
        </IconAction>
      </ButtonRow>
    </Container>
  )
}

export default PdfViewer

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const IFrame = styled.iframe`
  flex: 1;
  min-height: 0;
  width: 100%;
  border: 0;
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.line2};
`

const ButtonRow = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  padding-top: 0.25rem;
`

const IconAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.ink2};
  border-radius: ${({ theme }) => theme.radii.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme, $danger }) =>
      $danger ? theme.colors.danger : theme.colors.accent};
    color: ${({ theme, $danger }) =>
      $danger ? theme.colors.danger : theme.colors.ink};
    background: ${({ theme, $danger }) =>
      $danger ? theme.colors.line2 : theme.colors.accentSoft};
  }

  &:active {
    transform: scale(0.96);
  }
`
